import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, TextField, MenuItem, Button, CircularProgress,
  Alert, Chip, LinearProgress, Avatar, Divider, IconButton, Tooltip,
} from "@mui/material";
import {
  CloudUpload, CheckCircle, Close as CloseIcon, LinkOff,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import SkillRatingInput from "./SkillRatingInput";
import { COLORS, TYPE, RADIUS, CONTROL_HEIGHT } from "../../../styles/recruiterTheme";
import PublicPageShell, {
  PublicCard, PublicTitle, PublicStatus, readJson, networkMessage, validateFile, formatSize, MAX_UPLOAD_MB,
} from "../PublicPageShell";
const BASE = process.env.REACT_APP_API_BENCH_URL;

const NOTICES          = ["Immediate", "15 days", "30 days", "60 days", "90 days"];
const EMPLOYMENT_TYPES = ["Permanent", "Contract", "C2H", "Freelance"];

const EMPTY_FORM = {
  name: "", email: "", phone: "", current_role: "", skills: [],
  experience: "", location: "", current_salary: "", expected_salary: "",
  notice_period: "Immediate", last_client: "", last_project: "",
  employment_type: "Permanent", notes: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result.split(",")[1]);
  r.onerror = () => rej(new Error("Read failed"));
  r.readAsDataURL(file);
});

// Two columns on tablet/desktop, one on phones.
const grid2 = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 };
const sectionLabel = { fontWeight: 700, fontSize: TYPE.small, color: COLORS.primaryDark };

export default function BenchCandidateForm() {
  const { token } = useParams();
  const fileRef   = useRef(null);
  const parseSeq  = useRef(0);       // ignores a parse result if the file was removed/replaced meanwhile
  const inFlight  = useRef(false);

  const [meta,      setMeta]      = useState(null);       // { label, expires_at }
  const [metaError, setMetaError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [formData,  setFormData]  = useState(EMPTY_FORM);
  const [pdfFile,   setPdfFile]   = useState(null);
  const [fileId,    setFileId]    = useState("");
  const [parsing,   setParsing]   = useState(false);
  const [parseMsg,  setParseMsg]  = useState("");
  const [fileError, setFileError] = useState("");
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [touched,   setTouched]   = useState({});
  const [done,      setDone]      = useState(false);
  const [submittedId, setSubmittedId] = useState("");

  // Validate token on load
  useEffect(() => {
    let cancelled = false;
    setMetaError(""); setMeta(null);
    fetch(`${BASE}/public/bench-form/${token}`)
      .then(readJson)
      .then(res => {
        if (cancelled) return;
        if (!res.success) setMetaError(res.message || "This link is invalid or has expired.");
        else setMeta(res);
      })
      .catch(e => { if (!cancelled) setMetaError(networkMessage(e)); });
    return () => { cancelled = true; };
  }, [token, reloadKey]);

  const handleChange = e =>
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleBlur = e => setTouched(t => ({ ...t, [e.target.name]: true }));

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    const msg = validateFile(file, [".pdf"]);
    if (msg) { setFileError(msg); return; }
    setFileError("");
    const seq = ++parseSeq.current;
    setPdfFile(file);
    setFileId("");
    setParsing(true);
    setParseMsg("AI is reading your resume…");
    try {
      const b64 = await toBase64(file);
      const res = await fetch(`${BASE}/public/bench-form/${token}/parse-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_b64: b64 }),
      }).then(readJson);
      if (seq !== parseSeq.current) return;

      if (res.success && res.data) {
        // Only take non-empty parsed values so nulls never land in controlled inputs.
        const parsed = {};
        Object.entries(res.data).forEach(([k, v]) => {
          if (k === "skills" || !(k in EMPTY_FORM) || v === null || v === undefined || v === "") return;
          parsed[k] = typeof v === "number" ? String(v) : v;
        });
        let skills;
        if (typeof res.data.skills === "string") {
          skills = res.data.skills.split(",").map(x => x.trim()).filter(Boolean).map(name => ({ name, rating: 3 }));
        } else if (Array.isArray(res.data.skills)) {
          skills = res.data.skills;
        }
        setFormData(p => ({ ...p, ...parsed, ...(skills ? { skills } : {}) }));
        setFileId(res.file_id || "");
        setParseMsg("Details auto-filled from your resume — please review and correct if needed.");
      } else {
        setFileId(res.file_id || "");
        setParseMsg("Could not auto-read resume — please fill in manually.");
      }
    } catch {
      if (seq === parseSeq.current) setParseMsg("Could not auto-read resume — please fill in manually.");
    } finally {
      if (seq === parseSeq.current) setParsing(false);
    }
  };

  const removeFile = () => {
    parseSeq.current += 1;            // drop any in-flight parse result
    setPdfFile(null); setFileId(""); setParseMsg(""); setParsing(false); setFileError("");
  };

  const nameMissing  = !formData.name.trim();
  const emailInvalid = !EMAIL_RE.test(formData.email.trim());

  const handleSubmit = async () => {
    if (inFlight.current) return;
    setError("");
    setTouched(t => ({ ...t, name: true, email: true }));
    if (nameMissing || !formData.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    if (emailInvalid) {
      setError("Please enter a valid email address.");
      return;
    }
    const exp = Number(formData.experience);
    if (formData.experience !== "" && (Number.isNaN(exp) || exp < 0)) {
      setError("Experience must be a positive number of years.");
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/public/bench-form/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          name:  formData.name.trim(),
          email: formData.email.trim(),
          experience:      formData.experience      ? Number(formData.experience)      : 0,
          current_salary:  formData.current_salary  ? Number(formData.current_salary)  : 0,
          expected_salary: formData.expected_salary ? Number(formData.expected_salary) : 0,
          file_id: fileId,
        }),
      }).then(readJson);

      if (!res.success) throw new Error(res.message || "Submission failed");
      setSubmittedId(res.bench_id || "");
      setDone(true);
    } catch (err) {
      setError(networkMessage(err));
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  };

  // ── Invalid / expired link ─────────────────────────────────────────────────
  if (metaError) {
    const network = /reach the server|HTTP 5/.test(metaError);
    return (
      <PublicPageShell subtitle="Candidate profile">
        <PublicStatus tone="error" icon={<LinkOff />} title={network ? "Could not load this page" : "Link unavailable"}
          message={network ? metaError : `${metaError} Please contact the person who sent you this link.`}
          action={network ? { label: "Try again", onClick: () => setReloadKey(k => k + 1) } : null} />
      </PublicPageShell>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (!meta) return (
    <PublicPageShell subtitle="Candidate profile">
      <PublicStatus loading message="Loading the form…" />
    </PublicPageShell>
  );

  // ── Success ────────────────────────────────────────────────────────────────
  if (done) return (
    <PublicPageShell subtitle="Candidate profile">
      <PublicStatus tone="success" icon={<CheckCircle />} title="Profile submitted"
        message="Your profile has been received. Our team will review it and reach out soon." />
      {submittedId && (
        <Box textAlign="center" mt={-1}>
          <Chip label={`Reference: ${submittedId}`} variant="outlined" color="success" />
        </Box>
      )}
    </PublicPageShell>
  );

  // ── Form ───────────────────────────────────────────────────────────────────
  const expires = meta.expires_at ? new Date(meta.expires_at) : null;
  return (
    <PublicPageShell subtitle="Candidate profile">
      <PublicTitle title={meta.label || "Share your profile"}
        subtitle="Fill in your details below. Upload your resume and AI will fill most of the form for you." />
      {expires && !Number.isNaN(expires.getTime()) && (
        <Chip label={`Link valid till ${expires.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
          size="small" variant="outlined" sx={{ mt: -1, mb: 2, color: COLORS.muted, borderColor: COLORS.borderStrong }} />
      )}

      {/* Resume Upload */}
      <PublicCard>
        <Typography sx={{ ...sectionLabel, mb: 1.5 }}>
          Upload resume (optional — AI will auto-fill your details)
        </Typography>
        <Box role="button" tabIndex={0}
          onClick={() => !parsing && fileRef.current?.click()}
          onKeyDown={e => { if ((e.key === "Enter" || e.key === " ") && !parsing) { e.preventDefault(); fileRef.current?.click(); } }}
          sx={{
            border: `2px ${pdfFile ? "solid" : "dashed"} ${pdfFile ? COLORS.success : COLORS.borderStrong}`,
            borderRadius: `${RADIUS.md}px`, p: 2, display: "flex", alignItems: "center", gap: 2,
            cursor: parsing ? "default" : "pointer", bgcolor: pdfFile ? COLORS.successSoft : COLORS.surfaceTint,
            transition: "background-color 0.2s", "&:hover": parsing ? {} : { bgcolor: pdfFile ? COLORS.successSoft : COLORS.accentSoft },
          }}>
          <Avatar sx={{ width: 44, height: 44, bgcolor: pdfFile ? COLORS.surface : COLORS.accentSoft, flexShrink: 0 }}>
            {pdfFile ? <CheckCircle sx={{ color: COLORS.success }} /> : <CloudUpload sx={{ color: COLORS.primary }} />}
          </Avatar>
          <Box flex={1} minWidth={0}>
            {pdfFile
              ? <><Typography noWrap title={pdfFile.name} sx={{ fontWeight: 700, fontSize: TYPE.small, color: COLORS.success }}>{pdfFile.name}</Typography>
                  <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted }}>{formatSize(pdfFile.size)}</Typography></>
              : <><Typography sx={{ fontWeight: 600, fontSize: TYPE.small, color: COLORS.primary }}>Click to upload your resume</Typography>
                  <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted }}>PDF only · max {MAX_UPLOAD_MB} MB</Typography></>
            }
          </Box>
          {pdfFile && (
            <Tooltip title="Remove file">
              <IconButton size="small" aria-label="Remove file"
                onClick={e => { e.stopPropagation(); removeFile(); }}
                sx={{ color: COLORS.muted, "&:hover": { color: COLORS.error } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" hidden onChange={handleFileSelect} />
        </Box>
        {parsing && <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />}
        {fileError && <Alert severity="error" sx={{ mt: 1.5 }}>{fileError}</Alert>}
        {parseMsg && (
          <Typography sx={{ fontSize: TYPE.caption, mt: 1, display: "flex", alignItems: "center", gap: 0.5,
            color: parseMsg.includes("auto-filled") ? COLORS.success : COLORS.muted }}>
            {!parsing && parseMsg.includes("auto-filled") && <CheckCircle sx={{ fontSize: 14 }} />}
            {parseMsg}
          </Typography>
        )}
      </PublicCard>

      {/* Form Fields */}
      <PublicCard sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Typography sx={sectionLabel}>Personal info</Typography>
        <Box sx={grid2}>
          <TextField required fullWidth size="small" label="Full name" name="name"
            value={formData.name} onChange={handleChange} onBlur={handleBlur}
            error={!!touched.name && nameMissing} helperText={touched.name && nameMissing ? "Required" : ""} />
          <TextField required fullWidth size="small" type="email" label="Email" name="email"
            value={formData.email} onChange={handleChange} onBlur={handleBlur}
            error={!!touched.email && emailInvalid}
            helperText={touched.email && emailInvalid ? (formData.email.trim() ? "Enter a valid email" : "Required") : ""} />
          <TextField fullWidth size="small" type="tel" label="Phone" name="phone"
            value={formData.phone} onChange={handleChange} />
          <TextField fullWidth size="small" label="Location" name="location"
            value={formData.location} onChange={handleChange} />
        </Box>

        <Divider />
        <Typography sx={sectionLabel}>Professional details</Typography>
        <Box sx={grid2}>
          <TextField fullWidth size="small" label="Current role / designation" name="current_role"
            value={formData.current_role} onChange={handleChange} />
          <TextField fullWidth size="small" type="number" label="Experience (years)" name="experience"
            value={formData.experience} onChange={handleChange} inputProps={{ min: 0, step: 0.5 }} />
          <TextField select fullWidth size="small" label="Employment type" name="employment_type"
            value={formData.employment_type} onChange={handleChange}>
            {EMPLOYMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select fullWidth size="small" label="Notice period" name="notice_period"
            value={formData.notice_period} onChange={handleChange}>
            {/* keep a parsed value that is not in the list selectable instead of rendering blank */}
            {(NOTICES.includes(formData.notice_period) ? NOTICES : [...NOTICES, formData.notice_period])
              .map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
        </Box>
        <Box>
          <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, fontWeight: 600, mb: 1 }}>
            Skills &amp; proficiency (rate each 1–5)
          </Typography>
          <SkillRatingInput
            value={formData.skills}
            onChange={val => setFormData(p => ({ ...p, skills: val }))}
          />
        </Box>

        <Box sx={grid2}>
          <TextField fullWidth size="small" label="Last client" name="last_client"
            value={formData.last_client} onChange={handleChange} />
          <TextField fullWidth size="small" label="Last project" name="last_project"
            value={formData.last_project} onChange={handleChange} />
        </Box>

        <Divider />

        <TextField fullWidth multiline minRows={3} size="small" label="Anything else you'd like to share"
          name="notes" value={formData.notes} onChange={handleChange} />

        {error && <Alert severity="error">{error}</Alert>}

        <Button variant="contained" onClick={handleSubmit}
          disabled={saving || parsing}
          endIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ height: CONTROL_HEIGHT.lg, fontWeight: 600, textTransform: "none" }}>
          {saving ? "Submitting…" : parsing ? "Reading resume…" : "Submit my profile"}
        </Button>

        <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, textAlign: "center" }}>
          Your information will be reviewed by our recruitment team. We'll reach out if there's a match.
        </Typography>
      </PublicCard>
    </PublicPageShell>
  );
}
